/**
 * Shared Resource Detection Utility
 * 
 * Extracts resource requirements from plugin manifest pages/features.
 * Used by both PreviewRequirementsDialog and PreviewWithPageTabs/PreviewResourceManager.
 */

export interface ResourceRequirement {
  name: string;
  resourceName: string;
  resourceType: 'product' | 'variant' | 'price' | 'offer' | 'orderBump' | 'order' | 'checkoutSession';
  required: boolean;
  label?: string;
  isStatic: boolean; // true = saved to funnel, false = passed to preview session
  paramName: string; // The actual parameter name (e.g., "orderId" for path params)
}

export interface ManualRequirement {
  name: string;
  type: 'query' | 'path' | 'context' | 'static';
  required: boolean;
  resourceName: string;
}

export interface DetectedRequirements {
  resourceRequirements: ResourceRequirement[];
  manualRequirements: ManualRequirement[];
  pathParamMap: Record<string, string>;
}

/**
 * Detect resource type from requirement or parameter name
 */
export function detectResourceType(req: any): ResourceRequirement['resourceType'] | null {
  const resource = req.resource?.toLowerCase() || req.resourceName?.toLowerCase() || '';

  if (resource.includes('checkout') || resource.includes('session')) return 'checkoutSession';
  if (resource.includes('order') && !resource.includes('bump')) return 'order';
  if (resource.includes('offer')) return 'offer';
  if (resource.includes('product')) return 'product';
  if (resource.includes('variant')) return 'variant';
  if (resource.includes('price')) return 'price';
  if (resource.includes('bump') || resource.includes('orderbump')) return 'orderBump';

  return null;
}

/**
 * Get user-friendly label for resource type
 */
export function getResourceLabel(resourceType: string): string {
  const labels: Record<string, string> = {
    product: 'Product',
    variant: 'Variant',
    price: 'Price',
    offer: 'Offer',
    orderBump: 'Order Bump',
    order: 'Order',
    checkoutSession: 'Checkout Session',
  };
  return labels[resourceType] || resourceType;
}

/**
 * Extract resource requirements from plugin manifest pages
 * 
 * @param pages - Array of page definitions from plugin manifest
 * @param pagePath - The specific page path to analyze (e.g., "/checkout", "/thankyou")
 * @param internalPath - Optional internal path override
 * @returns Detected requirements
 */
export function detectResourceRequirements(
  pages: any[] | undefined,
  pagePath?: string,
  internalPath?: string
): DetectedRequirements {
  if (!pages || pages.length === 0) {
    return { resourceRequirements: [], manualRequirements: [], pathParamMap: {} };
  }

  // Find the matching page
  let page: any = null;

  if (pagePath || internalPath) {
    const urlPath = internalPath
      ? internalPath.replace(/^\//, '')
      : pagePath?.replace(/^\//, '') || '';

    page = pages.find((p: any) => {
      const pPath = p.path.replace(/^\//, '');
      return pPath === urlPath || p.path === `/${urlPath}`;
    });
  } else {
    // If no specific path, use first page
    page = pages[0];
  }

  if (!page?.features) {
    return { resourceRequirements: [], manualRequirements: [], pathParamMap: {} };
  }

  // Extract path params from page path (e.g., /thankyou/:orderId -> { orderId: true })
  const pathParamMap: Record<string, string> = {};
  const pathMatches = page.path.matchAll(/:(\w+)/g);
  for (const match of pathMatches) {
    const paramName = match[1];
    pathParamMap[paramName] = paramName;
  }

  const resources: ResourceRequirement[] = [];
  const manual: ManualRequirement[] = [];
  // Track seen resources by resourceName to prevent duplicates
  const seenResources = new Set<string>();

  page.features.forEach((feature: any) => {
    feature.requirements?.forEach((req: any) => {
      const resourceType = detectResourceType(req);

      if (resourceType) {
        // This requirement has a resource type
        const resourceName = req.resource || req.from?.[0]?.name;
        if (!resourceName) return;

        const resourceNameKey = resourceName.toLowerCase();

        // Skip if we've already seen this resource
        if (seenResources.has(resourceNameKey)) {
          return;
        }

        seenResources.add(resourceNameKey);

        if (!req.from || req.from.length === 0) {
          // No params defined, skip
          return;
        }

        // Prioritize param types for preview: query > static > path > context
        // This ensures we use the most appropriate param for URL building
        const paramPriority: Record<string, number> = {
          query: 1,
          static: 2,
          path: 3,
          context: 4,
        };

        // Sort params by priority and find the best one for preview
        const sortedParams = [...req.from].sort((a, b) => {
          const priorityA = paramPriority[a.type] || 99;
          const priorityB = paramPriority[b.type] || 99;
          return priorityA - priorityB;
        });

        const bestParam = sortedParams[0];

        // Path params can also be resources (e.g., :orderId -> order resource)
        // We should include them, but they're not static
        const isStatic =
          bestParam.type === 'static' ||
          (bestParam.type !== 'path' &&
            ['product', 'variant', 'price', 'offer', 'orderBump'].includes(resourceType));

        // For path params, use the actual path param name (e.g., "orderId") instead of param.name (e.g., "id")
        let displayName = bestParam.name;
        let paramName = bestParam.name;

        if (bestParam.type === 'path') {
          // Find matching path param from page path
          const pathParamName = Object.keys(pathParamMap).find(
            (key) =>
              key.toLowerCase() === resourceName.toLowerCase() ||
              key.toLowerCase().includes(resourceType),
          );
          if (pathParamName) {
            displayName = pathParamName;
            paramName = pathParamName;
          }
        }

        resources.push({
          name: displayName, // Use path param name for path params
          resourceName,
          resourceType,
          required: bestParam.required !== false,
          label: req.label || getResourceLabel(resourceType),
          isStatic,
          paramName, // Store the actual param name for URL building
        });
      } else {
        // No resource type - treat as manual requirement
        if (req.from) {
          req.from.forEach((param: any) => {
            // Only add to manual if it's not a path param (path params are handled in URL building)
            if (param.type !== 'path') {
              manual.push({
                name: param.name,
                type: param.type,
                required: param.required !== false,
                resourceName: req.resource || param.name,
              });
            }
          });
        }
      }
    });
  });

  return { resourceRequirements: resources, manualRequirements: manual, pathParamMap };
}

/**
 * Get the primary param type from detected requirements
 * This is used for backward compatibility with PreviewResourceManager
 */
export function getPrimaryParamType(requirements: DetectedRequirements): 'checkoutSession' | 'order' | 'offer' | 'none' {
  // Find the first non-static resource requirement
  const nonStaticResource = requirements.resourceRequirements.find((req) => !req.isStatic);

  if (nonStaticResource) {
    if (nonStaticResource.resourceType === 'checkoutSession') return 'checkoutSession';
    if (nonStaticResource.resourceType === 'order') return 'order';
    if (nonStaticResource.resourceType === 'offer') return 'offer';
  }

  return 'none';
}
